import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { IResponseHandlerOptions, useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreatePhysicalDistribution,
  IUpdatePhysicalDistribution,
} from './types';
import { IDistributionResponse, ITemplatesLightListItem } from '../types';

export const useGetPhysicalDistributionTemplateLightList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['physical-distribution-template-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `PhysicalDistributionSectionTemplate/LightList`,
      });

      return responseHandler(response, {
        groupName: `useGetPhysicalDistributionTemplateLightList`,
        notFoundPath: '/templates',
      });
    },
    enabled: enabled,
  });
};

export const useGetPhysicalDistributionSectionTemplate =  (
  id: string,
  options?: IResponseHandlerOptions<IDistributionResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDistributionResponse, DefaultError>({
    queryKey: ['physical-distribution-template', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `PhysicalDistributionSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetPhysicalDistributionSectionTemplate - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const usePostPhysicalDistributionTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostPhysicalDistributionTemplate'
  );
  return useMutation<string, DefaultError, ICreatePhysicalDistribution>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'PhysicalDistributionSectionTemplate',
        body: { physicalDistributionSectionTemplate: body },
      });
      
      return responseHandler(response, {
        groupName: 'usePostPhysicalDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutPhysicalDistributionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutPhysicalDistributionTemplate'
  );
  return useMutation<string, DefaultError, IUpdatePhysicalDistribution>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'PhysicalDistributionSectionTemplate',
        body: {physicalDistributionSectionId: id, physicalDistributionSectionTemplate: body},
      });

      return responseHandler(response, {
        groupName: 'usePutPhysicalDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeletePhysicalDistributionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeletePhysicalDistributionTemplate'
  );
  return useMutation<string, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.remove.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `PhysicalDistributionSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeletePhysicalDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.remove.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
